import { useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SmsConsent } from "@/components/SmsConsent";
import { API_URL, BUSINESS, HELP_TOPICS, SMS_CONSENT_VERSION } from "@/lib/constants";

const initialFields = { full_name: "", email: "", phone: "", help_with: "" };

const validate = (fields) => {
  const errors = {};
  if (!fields.full_name.trim()) errors.full_name = "Please enter your full name.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.email.trim()))
    errors.email = "Please enter a valid email address.";
  if (!/^[+()\-.\s\d]{7,}$/.test(fields.phone.trim()))
    errors.phone = "Please enter a valid mobile phone number.";
  if (!fields.help_with) errors.help_with = "Please select what you would like help with.";
  return errors;
};

export const ConsultationForm = ({ source = "homepage-consultation-form" }) => {
  const [fields, setFields] = useState(initialFields);
  const [smsConsent, setSmsConsent] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const submittingRef = useRef(false);

  const setField = (key) => (e) => {
    setFields({ ...fields, [key]: e.target.value });
    if (errors[key]) setErrors({ ...errors, [key]: undefined });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submittingRef.current) return;
    const errs = validate(fields);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/consultations`, {
        full_name: fields.full_name.trim(),
        email: fields.email.trim(),
        phone: fields.phone.trim(),
        help_with: fields.help_with,
        sms_consent: smsConsent,
        source: "j11consulting.online",
        form_source: source,
        page_url: window.location.href,
        consent_version: SMS_CONSENT_VERSION,
      });
      setSubmitted(true);
    } catch (err) {
      toast.error(`We could not submit your request. Please try again or call us at ${BUSINESS.phone}.`);
      submittingRef.current = false;
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      id="consultation-form"
      data-testid="consultation-form-card"
      className="relative rounded-2xl border border-white/10 bg-[#17171C] p-7 shadow-xl shadow-black/40 sm:p-9"
    >
      <div className="absolute inset-x-8 top-0 h-1 rounded-b-full bg-[#C9A227]" />
      {submitted ? (
        <motion.div
          data-testid="consultation-success-message"
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="flex min-h-[420px] flex-col items-center justify-center text-center"
        >
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#C9A227]/10">
            <CheckCircle2 className="h-8 w-8 text-[#D8B54A]" />
          </span>
          <h3 className="mt-6 font-heading text-2xl font-bold tracking-tight text-[#F2EEE3]">Request received</h3>
          <p className="mt-3 max-w-sm text-sm leading-relaxed text-[#B8B2A6]">
            Thank you. Your request has been received. A member of the {BUSINESS.name} team will
            follow up with you.
          </p>
          <a
            href={BUSINESS.phoneHref}
            data-testid="consultation-success-call-link"
            className="mt-6 text-sm font-semibold text-[#D8B54A] hover:text-[#E4C463]"
          >
            Prefer to talk now? Call {BUSINESS.phone}
          </a>
        </motion.div>
      ) : (
        <>
          <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#D8B54A]" data-testid="consultation-form-label">
            Free Consultation
          </p>
          <h3 className="mt-2 font-heading text-2xl font-bold tracking-tight text-[#F2EEE3] sm:text-3xl">
            Request Your Free Consultation
          </h3>
          <p className="mt-2 text-sm leading-relaxed text-[#9E978A]">
            Tell us what you would like help understanding and a member of the {BUSINESS.shortName}{" "}
            team will follow up with you.
          </p>
          <form onSubmit={handleSubmit} noValidate className="mt-7 space-y-5" data-testid="consultation-form">
            <div>
              <Label htmlFor="consultation-name" className="text-sm font-medium text-[#F2EEE3]">
                Full Name
              </Label>
              <Input
                id="consultation-name"
                data-testid="consultation-name-input"
                value={fields.full_name}
                onChange={setField("full_name")}
                placeholder="Jane Smith"
                autoComplete="name"
                className="mt-1.5 h-11 border-white/25 bg-transparent focus-visible:ring-[#C9A227]"
              />
              {errors.full_name && (
                <p className="mt-1.5 text-xs text-red-600" data-testid="consultation-name-error">{errors.full_name}</p>
              )}
            </div>
            <div>
              <Label htmlFor="consultation-email" className="text-sm font-medium text-[#F2EEE3]">
                Email Address
              </Label>
              <Input
                id="consultation-email"
                type="email"
                data-testid="consultation-email-input"
                value={fields.email}
                onChange={setField("email")}
                placeholder="jane@example.com"
                autoComplete="email"
                className="mt-1.5 h-11 border-white/25 bg-transparent focus-visible:ring-[#C9A227]"
              />
              {errors.email && (
                <p className="mt-1.5 text-xs text-red-600" data-testid="consultation-email-error">{errors.email}</p>
              )}
            </div>
            <div>
              <Label htmlFor="consultation-phone" className="text-sm font-medium text-[#F2EEE3]">
                Mobile Phone Number
              </Label>
              <Input
                id="consultation-phone"
                type="tel"
                data-testid="consultation-phone-input"
                value={fields.phone}
                onChange={setField("phone")}
                placeholder="(555) 123-4567"
                autoComplete="tel"
                className="mt-1.5 h-11 border-white/25 bg-transparent focus-visible:ring-[#C9A227]"
              />
              {errors.phone && (
                <p className="mt-1.5 text-xs text-red-600" data-testid="consultation-phone-error">{errors.phone}</p>
              )}
            </div>
            <div>
              <Label htmlFor="consultation-help-with" className="text-sm font-medium text-[#F2EEE3]">
                What would you like help with?
              </Label>
              <Select
                value={fields.help_with}
                onValueChange={(val) => {
                  setFields({ ...fields, help_with: val });
                  if (errors.help_with) setErrors({ ...errors, help_with: undefined });
                }}
              >
                <SelectTrigger
                  id="consultation-help-with"
                  data-testid="consultation-help-with-select"
                  className="mt-1.5 h-11 border-white/25 bg-transparent focus:ring-[#C9A227]"
                >
                  <SelectValue placeholder="Select a topic" />
                </SelectTrigger>
                <SelectContent>
                  {HELP_TOPICS.map((topic) => (
                    <SelectItem
                      key={topic}
                      value={topic}
                      data-testid={`consultation-topic-${topic.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    >
                      {topic}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.help_with && (
                <p className="mt-1.5 text-xs text-red-600" data-testid="consultation-help-with-error">{errors.help_with}</p>
              )}
            </div>
            <SmsConsent
              id="consultation-sms-consent"
              testId="consultation-sms-consent-checkbox"
              checked={smsConsent}
              onCheckedChange={setSmsConsent}
            />
            <button
              type="submit"
              data-testid="consultation-submit-button"
              disabled={submitting}
              className="group flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-b from-[#E7C766] to-[#AE8924] text-sm font-semibold text-black shadow-sm transition-colors duration-200 hover:from-[#F2D47E] hover:to-[#C49B2E] disabled:opacity-60"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  Request Consultation
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                </>
              )}
            </button>
            <p className="text-center text-[13px] leading-relaxed text-[#9E978A]" data-testid="consultation-no-auto-sms-note">
              Submitting this form without selecting the SMS checkbox does not enroll you in SMS
              messaging.
            </p>
          </form>
        </>
      )}
    </div>
  );
};
